// Service for social media API integrations
import { getCurrentUser, getEffectiveAccountId } from './authorizationService';

class SocialMediaApiService {
  constructor() {
    this.baseUrl = 'https://google.shift.healthcare/api';
  }

  // Generic API call method
  async makeApiCall(endpoint, options = {}) {
    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        headers: {
          'Content-Type': 'application/json',
          ...options.headers
        },
        ...options
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `API call failed: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Social Media API Error:', error);
      throw error;
    }
  }

  // Facebook/Meta API methods
  async connectFacebook() {
    // Get current user ID from auth context or localStorage
    const userId = this.getCurrentUserId();
    if (!userId) {
      throw new Error('User not authenticated');
    }

    // Use the worker endpoint for OAuth
    const response = await this.makeApiCall(`/social/facebook/connect?userId=${userId}`);
    
    // The worker will return a redirect URL, so we redirect to it
    if (response.redirectUrl) {
      window.location.href = response.redirectUrl;
    } else {
      throw new Error('Failed to get Facebook OAuth URL');
    }
  }

  // Helper method to get current user ID
  getCurrentUserId() {
    const currentUser = getCurrentUser();
    if (!currentUser) {
      throw new Error('User not authenticated');
    }
    
    // For admin scoped to client account, use the effective account ID
    // For regular users, use their own UID
    const effectiveAccountId = getEffectiveAccountId();
    return effectiveAccountId || currentUser.uid;
  }

  async postToFacebook(pageId, message, imageUrl = null) {
    return await this.makeApiCall('/social/facebook/post', {
      method: 'POST',
      body: JSON.stringify({
        pageId,
        message,
        imageUrl
      })
    });
  }

  async getFacebookMetrics(pageId, postId) {
    return await this.makeApiCall(`/social/facebook/metrics?pageId=${pageId}&postId=${postId}`);
  }

  // Twitter/X API methods
  async connectTwitter() {
    const userId = this.getCurrentUserId();
    return await this.makeApiCall(`/social/twitter/connect?userId=${userId}`, {
      method: 'POST'
    });
  }

  async postToTwitter(text, imageUrl = null) {
    return await this.makeApiCall('/social/twitter/post', {
      method: 'POST',
      body: JSON.stringify({
        text,
        imageUrl
      })
    });
  }

  async getTwitterMetrics(tweetId) {
    return await this.makeApiCall(`/social/twitter/metrics?tweetId=${tweetId}`);
  }

  // LinkedIn API methods
  async connectLinkedIn() {
    const userId = this.getCurrentUserId();
    return await this.makeApiCall(`/social/linkedin/connect?userId=${userId}`, {
      method: 'POST'
    });
  }

  async postToLinkedIn(text, imageUrl = null) {
    return await this.makeApiCall('/social/linkedin/post', {
      method: 'POST',
      body: JSON.stringify({
        text,
        imageUrl
      })
    });
  }

  async getLinkedInMetrics(postId) {
    return await this.makeApiCall(`/social/linkedin/metrics?postId=${postId}`);
  }

  // Instagram API methods (via Facebook)
  async postToInstagram(mediaUrl, caption) {
    return await this.makeApiCall('/social/instagram/post', {
      method: 'POST',
      body: JSON.stringify({
        mediaUrl,
        caption
      })
    });
  }

  async getInstagramMetrics(mediaId) {
    return await this.makeApiCall(`/social/instagram/metrics?mediaId=${mediaId}`);
  }

  // Generic posting method
  async postToPlatform(platform, postData) {
    switch (platform) {
      case 'facebook':
        return await this.postToFacebook(postData.pageId, postData.message, postData.imageUrl);
      case 'twitter':
      case 'x':
        return await this.postToTwitter(postData.text, postData.imageUrl);
      case 'linkedin':
        return await this.postToLinkedIn(postData.text, postData.imageUrl);
      case 'instagram':
        return await this.postToInstagram(postData.imageUrl, postData.caption);
      default:
        throw new Error(`Unsupported platform: ${platform}`);
    }
  }

  // Generic metrics method
  async getPlatformMetrics(platform, postId, additionalParams = {}) {
    switch (platform) {
      case 'facebook':
        return await this.getFacebookMetrics(additionalParams.pageId, postId);
      case 'twitter':
      case 'x':
        return await this.getTwitterMetrics(postId);
      case 'linkedin':
        return await this.getLinkedInMetrics(postId);
      case 'instagram':
        return await this.getInstagramMetrics(postId);
      default:
        throw new Error(`Unsupported platform: ${platform}`);
    }
  }

  // Check connection status for all platforms
  async getConnectionStatus() {
    return await this.makeApiCall('/social/connections/status');
  }

  // Disconnect a platform
  async disconnectPlatform(platform) {
    return await this.makeApiCall(`/social/${platform}/disconnect`, {
      method: 'POST'
    });
  }

  // Get available pages/accounts for a platform
  async getAvailableAccounts(platform) {
    return await this.makeApiCall(`/social/${platform}/accounts`);
  }

  // Format post data for different platforms
  formatPostForPlatform(platform, post) {
    switch (platform) {
      case 'facebook':
        return {
          pageId: post.pageId || 'default',
          message: post.content,
          imageUrl: post.imageUrl
        };
      case 'twitter':
      case 'x':
        return {
          text: post.content,
          imageUrl: post.imageUrl
        };
      case 'linkedin':
        return {
          text: post.content,
          imageUrl: post.imageUrl
        };
      case 'instagram':
        return {
          imageUrl: post.imageUrl,
          caption: post.content
        };
      default:
        return post;
    }
  }

  // Validate post content for platform limits
  validatePostContent(platform, content) {
    const limits = {
      facebook: 63206,
      twitter: 280,
      x: 280,
      linkedin: 3000,
      instagram: 2200
    };

    const limit = limits[platform];
    if (!limit) return { isValid: true };

    return {
      isValid: content.length <= limit,
      limit,
      current: content.length,
      remaining: limit - content.length
    };
  }
}

const socialMediaApiService = new SocialMediaApiService();
export default socialMediaApiService;
